import type { ApiClient } from 'src/store/backend';
import {
  currentEmployeeUuid,
  employeeCompetenceAsListItems,
  employeeContractsAsListItems,
  employeesForStaffingCalendar,
  employeesNotDeleted,
  functionsNotDeleted,
  getAssociationTypes,
  getOrganization,
  holidaysForStaffingCalendar,
  key,
  professions,
  sector,
  timekeepingPeriods,
  toEmployeeViewModel,
  writeAccess,
} from 'src/store/selectors';
import type { WorkSchedule } from '@trinnvis/dabih-api-client';
import { BASE_PATH, EmployeeViewModelStatusEnum, FunctionViewModelTypeEnum } from '@trinnvis/dabih-api-client';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { sortBy } from 'lodash';
import { calculateBalance } from 'src/utilities/time-balance/time-balance';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import { defaultWorkSchedule } from '../../store/selectors/staffing/default-work-schedule';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { EmployeeViewEditItem } from '../../content/employees/employee-view-edit-item';
import type { EmployeeView } from '../../content/employees/employee-view';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { getUsersForAccess } from '../get-users-for-access';
import { workHoursDescription } from 'src/store/selectors/employees/work-hours-description';
import { t } from 'src/i18n';
import { getTimekeepingOutsideEmploymentPeriodWarning } from 'src/utilities/time-balance/timekeeping-before-employment-start-warning';

function currentSchedule(workSchedules: WorkSchedule[]): WorkSchedule {
  let result = defaultWorkSchedule;
  const sortedWorkSchedules = sortBy(workSchedules, [(item) => item.start]);
  sortedWorkSchedules.forEach(function (schedule) {
    if (LocalDate.fromString(schedule.start).isSameOrBefore(LocalDate.now())) {
      result = schedule;
    }
  });
  return result;
}
export async function buildEmployeeView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<EmployeeView> {
  let leavePeriodEditRestriction = false;
  const organization = getOrganization(state);
  if (organization) {
    leavePeriodEditRestriction = organization.leavePeriodEditRestriction;
  }
  assertIsDefined(organization);

  const item = organization.employeesById[uuid];
  assertEntityFound(item, 'employees', uuid);

  const entity = toEmployeeViewModel(item, organization);
  const href = currentParent + '/employees/' + uuid;

  const current = currentEmployeeUuid(state);

  // Build tutorials list - show completed and in-progress courses for this employee
  const tutorials: ListSectionItemInput[] = [];
  const today = LocalDate.fromString(state.today);

  if (organization.tutorials) {
    organization.tutorials.forEach((tutorial) => {
      // Only show courses (not guides - guides don't have employeeUuid)
      if (tutorial.guide) return;

      if (tutorial.registrations && tutorial.registrations.length > 0) {
        tutorial.registrations.forEach((registration) => {
          if (!registration.participants) return;

          registration.participants.forEach((participant) => {
            // Match this employee
            if (participant.employeeUuid === uuid) {
              let secondaryLabel = t('tutorials.waitingForCompletion');
              let mainIcon: 'checkBlue' | 'alert' | undefined = undefined;

              // Completed - show completion date
              if (participant.completed) {
                const completedDate = LocalDate.fromString(participant.completed);
                mainIcon = 'checkBlue';
                secondaryLabel = t('tutorials.completedOnDate', { date: completedDate.toStringForDisplay() });
              }
              // Not completed but has due date
              else if (registration.dueDate) {
                const dueDate = LocalDate.fromString(registration.dueDate);
                // Overdue
                if (dueDate.isBefore(today)) {
                  mainIcon = 'alert';
                  secondaryLabel = t('library.tutorialParticipants.notCompletedInTime');
                }
                // Still time - show due date
                else {
                  secondaryLabel = t('tutorials.waitingForCompletionWithDueDate', {
                    date: dueDate.toStringForDisplay(),
                  });
                }
              }

              tutorials.push({
                label: tutorial.title,
                secondaryLabel: secondaryLabel,
                accessible: false,
                mainIcon,
                clickData: tutorial.id, // For opening tutorial viewer
              });
            }
          });
        });
      }
    });
  }

  // Sort tutorials alphabetically by title
  tutorials.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

  const forStaffing = employeesForStaffingCalendar(state).find((e) => e.uuid === entity.uuid);
  const currentUserHasStaffingAccess = organization.staffingCalendarAccessList.includes(current ?? '');

  const timekeepingPeriodsForEmployee = timekeepingPeriods(
    employeesForStaffingCalendar(state),
    uuid,
    holidaysForStaffingCalendar(),
  );
  const timekeepingBeforeEmploymentStartWarning = forStaffing
    ? getTimekeepingOutsideEmploymentPeriodWarning([
        {
          uuid,
          name: entity.name ?? '',
          periods: timekeepingPeriodsForEmployee,
          forStaffing,
        },
      ])
    : undefined;

  const attachments1 = toAttachmentItems(api, 'employees', entity.uuid, entity.attachments);
  const employmentStartDate = entity.employmentStartDate ?? '';
  return {
    ...commonData,
    promoData: getPromoDataForEntity('employees', item, getFeatureStates(state)),
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasStaffingAccess: currentUserHasStaffingAccess,
    currentUserHasWriteAccess: writeAccess(state),

    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: '',
    href: currentParent + '/employees/' + uuid,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/employees/' + uuid + '.pdf?key=' + key(state),
    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'employees',
    name: entity.name ?? '',
    attachments: attachments1,
    profession: entity.profession ?? '',
    associationType: entity.associationType ?? '',
    accessLevel: entity.accessLevel ?? '',
    personalIdentifier: entity.personalIdentifier ?? '',
    hprNumber: entity.hprNumber ?? '',
    herNumber: entity.herNumber ?? '',
    status: entity.status ?? '',
    employmentStartDate: employmentStartDate,
    employmentEndDate: entity.employmentEndDate ?? '',
    phone: entity.phone ?? '',
    secondaryPhone: entity.secondaryPhone ?? '',
    email: entity.email ?? '',
    address: entity.address ?? '',
    gender: entity.gender ?? '',
    notes: entity.notes ?? '',
    nextOfKin: entity.nextOfKin ?? '',
    workHoursDescription: workHoursDescription(currentSchedule(entity.workSchedules)),
    assignedFunctions: sortBy(
      entity.assignedFunctions.map((f) => ({
        name: f.name ?? '',
        assignedEmployeeNames: f.assignedEmployeeNames,
        type: f.type,
        href: currentParent + '/employees/' + uuid + '/functions/' + f.uuid,
        uuid: f.uuid,
        hasDraft: f.hasDraft,
      })),
      [(f) => f.type !== FunctionViewModelTypeEnum.Common, (f) => f.name],
    ),
    unassignedFunctions: entity.unassignedFunctions.map((f) => ({
      name: f.name ?? '',
      assignedEmployeeNames: f.assignedEmployeeNames,
      type: f.type,
      href: currentParent + '/employees/' + uuid + '/functions/' + f.uuid,
      uuid: f.uuid,
      hasDraft: f.hasDraft,
    })),
    contracts: employeeContractsAsListItems(state, href, uuid),
    competence: employeeCompetenceAsListItems(state, href, uuid),
    isCurrentUser: current === uuid,
    isCurrentOwner: organization.ownerEmail === entity.email,
    firstName: entity.firstName,
    lastName: entity.lastName,
    expertise: entity.expertise ?? '',
    employeesForReassignment: employeesNotDeleted(state)
      .filter((e) => e.status === EmployeeViewModelStatusEnum.Active)
      .filter((e) => e.uuid !== uuid)
      .map((e) => ({ uuid: e.uuid, firstName: e.firstName, lastName: e.lastName })),
    functionsForReassignment: functionsNotDeleted(state)
      .filter((funct) => {
        const foundInEmployees = funct.employees.filter((employee) => employee === uuid);
        return funct.type !== 'COMMON' && foundInEmployees.length > 0;
      })
      .map((f) => ({ uuid: f.uuid, name: f.name ?? '', type: f.type.toString() })),
    inactiveEmployeeIdForReassignment: uuid,
    tutorials: tutorials,
    today: LocalDate.fromString(state.today),
    speciality: entity.speciality ?? '',
    deletable: (current !== uuid && organization.ownerEmail !== entity.email && entity.deletable) ?? false,
    forStaffing: forStaffing,
    otherUserEmails: getUsersForAccess(state)
      .filter((e) => e.uuid !== uuid)
      .map((e) => e.email ?? '')
      .filter((x) => x !== ''),
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'employees', uuid, organization.organizationId);
      return command.draft as EmployeeViewEditItem;
    },
    timekeepingPeriods: timekeepingPeriodsForEmployee,
    timekeepingBalance: calculateBalance(
      timekeepingPeriodsForEmployee,
      entity.employmentStartDate,
      entity.employmentEndDate,
    ),
    timekeepingEmployee: forStaffing,
    timekeepingBeforeEmploymentStartWarning,
    leavePeriodEditRestriction,
    featureStates: getFeatureStates(state),
    sector: sector(state),
    professions: professions(state),
    associationTypes: getAssociationTypes(sector(state)),
  };
}
